import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { 
  History,
  User,
  FileText,
  MessageSquare,
  Settings,
  LogIn,
  LogOut,
  Edit,
  Trash2,
  Download,
  Eye,
  Search,
  Filter,
  RefreshCw,
  Calendar,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const AuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    action: '',
    resource_type: '',
    user_id: '',
    search: ''
  });
  const [pagination, setPagination] = useState({ limit: 50, skip: 0, total: 0 });

  useEffect(() => {
    fetchLogs();
    fetchSummary();
  }, [pagination.skip]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.action) params.append('action', filters.action);
      if (filters.resource_type) params.append('resource_type', filters.resource_type);
      if (filters.user_id) params.append('user_id', filters.user_id);
      params.append('limit', pagination.limit);
      params.append('skip', pagination.skip);

      const response = await axios.get(`${API}/api/v2/audit-logs?${params.toString()}`);
      setLogs(response.data.logs);
      setPagination(prev => ({ ...prev, total: response.data.total }));
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const response = await axios.get(`${API}/api/v2/audit-logs/summary`);
      setSummary(response.data);
    } catch (error) {
      console.error('Failed to fetch summary:', error);
    }
  };

  const applyFilters = () => {
    setPagination(prev => ({ ...prev, skip: 0 }));
    fetchLogs();
  };

  const clearFilters = () => {
    setFilters({ action: '', resource_type: '', user_id: '', search: '' });
    setPagination(prev => ({ ...prev, skip: 0 }));
    setTimeout(fetchLogs, 100);
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'login': return <LogIn className="w-4 h-4 text-green-600" />;
      case 'logout': return <LogOut className="w-4 h-4 text-slate-600" />;
      case 'create': case 'create_rfp': return <FileText className="w-4 h-4 text-blue-600" />;
      case 'update': case 'update_rfp': case 'update_settings': case 'update_rfp_progress': return <Edit className="w-4 h-4 text-amber-600" />;
      case 'delete': return <Trash2 className="w-4 h-4 text-red-600" />;
      case 'export': return <Download className="w-4 h-4 text-purple-600" />;
      case 'view': return <Eye className="w-4 h-4 text-slate-600" />;
      case 'comment': return <MessageSquare className="w-4 h-4 text-teal-600" />;
      default: return <History className="w-4 h-4 text-slate-600" />;
    }
  };

  const getActionBadgeColor = (action) => {
    switch (action) {
      case 'login': return 'bg-green-100 text-green-700';
      case 'logout': return 'bg-slate-100 text-slate-700';
      case 'create': case 'create_rfp': return 'bg-blue-100 text-blue-700';
      case 'update': case 'update_rfp': case 'update_settings': return 'bg-amber-100 text-amber-700';
      case 'delete': return 'bg-red-100 text-red-700';
      case 'export': return 'bg-purple-100 text-purple-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const totalPages = Math.ceil(pagination.total / pagination.limit);
  const currentPage = Math.floor(pagination.skip / pagination.limit) + 1;

  return (
    <div className="min-h-screen bg-slate-50 p-8" data-testid="audit-logs">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-800 flex items-center gap-3">
              <History className="w-8 h-8 text-indigo-600" />
              Audit Logs
            </h1>
            <p className="text-slate-500 mt-1">Track all user actions and system events</p>
          </div>
          <Button onClick={fetchLogs} variant="outline" disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Summary Stats */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="border-0 shadow-md">
              <CardContent className="p-4">
                <p className="text-2xl font-bold text-slate-800">{summary.total_logs}</p>
                <p className="text-sm text-slate-500">Total Logs</p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-md bg-blue-50">
              <CardContent className="p-4">
                <p className="text-2xl font-bold text-blue-700">{summary.today_activity}</p>
                <p className="text-sm text-blue-600">Today's Activity</p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-md">
              <CardContent className="p-4">
                <p className="text-2xl font-bold text-slate-800">{summary.top_users?.length || 0}</p>
                <p className="text-sm text-slate-500">Active Users</p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-md">
              <CardContent className="p-4">
                <p className="text-2xl font-bold text-slate-800">{Object.keys(summary.actions_by_type || {}).length}</p>
                <p className="text-sm text-slate-500">Action Types</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card className="border-0 shadow-md">
          <CardContent className="p-4">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-slate-500" />
                <span className="text-sm font-medium text-slate-600">Filters:</span>
              </div>
              
              <Select value={filters.action} onValueChange={(v) => setFilters(prev => ({ ...prev, action: v }))}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Action" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="login">Login</SelectItem>
                  <SelectItem value="logout">Logout</SelectItem>
                  <SelectItem value="create_rfp">Create RFP</SelectItem>
                  <SelectItem value="update_rfp">Update RFP</SelectItem>
                  <SelectItem value="update_rfp_progress">Update Progress</SelectItem>
                  <SelectItem value="update_settings">Update Settings</SelectItem>
                  <SelectItem value="delete">Delete</SelectItem>
                  <SelectItem value="export">Export</SelectItem>
                </SelectContent>
              </Select>

              <Select value={filters.resource_type} onValueChange={(v) => setFilters(prev => ({ ...prev, resource_type: v }))}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Resource" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="rfp">RFP</SelectItem>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="comment">Comment</SelectItem>
                  <SelectItem value="team">Team</SelectItem>
                  <SelectItem value="fine_tuning">Settings</SelectItem>
                </SelectContent>
              </Select>

              <Button onClick={applyFilters} size="sm" className="bg-[#007AFF]">
                Apply
              </Button>
              <Button onClick={clearFilters} variant="ghost" size="sm">
                Clear
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Logs Table */}
        <Card className="border-0 shadow-lg">
          <CardHeader className="pb-2">
            <CardTitle>Activity Log</CardTitle>
            <CardDescription>
              Showing {logs.length} of {pagination.total} entries
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase">Timestamp</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase">User</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase">Action</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase">Resource</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="text-center py-8 text-slate-500">
                        <History className="w-12 h-12 mx-auto mb-2 opacity-30" />
                        <p>No audit logs found</p>
                      </td>
                    </tr>
                  ) : (
                    logs.map((log) => (
                      <tr key={log.id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-slate-400" />
                            <span className="text-sm text-slate-600">{formatDate(log.created_at)}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center">
                              <User className="w-4 h-4 text-slate-600" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-slate-800">{log.user_name}</p>
                              <p className="text-xs text-slate-500">{log.user_email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            {getActionIcon(log.action)}
                            <Badge className={getActionBadgeColor(log.action)}>
                              {log.action?.replace(/_/g, ' ')}
                            </Badge>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div>
                            <p className="text-sm text-slate-800">{log.resource_type}</p>
                            {log.resource_name && (
                              <p className="text-xs text-slate-500 truncate max-w-[200px]">{log.resource_name}</p>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          {log.details && Object.keys(log.details).length > 0 && (
                            <Badge variant="outline" className="text-xs">
                              {Object.keys(log.details).length} fields
                            </Badge>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-200">
                <p className="text-sm text-slate-500">
                  Page {currentPage} of {totalPages}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPagination(prev => ({ ...prev, skip: Math.max(0, prev.skip - prev.limit) }))}
                    disabled={pagination.skip === 0}
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPagination(prev => ({ ...prev, skip: prev.skip + prev.limit }))}
                    disabled={pagination.skip + pagination.limit >= pagination.total}
                  >
                    Next
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AuditLogs;
